import React from "react";
import { shareLink } from "@/lib/pageMeta";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const LABELS = {
  feeling: { very_good: "Very good", good: "Good", okay: "Okay", not_well: "Not well" },
  biggest_challenge: { meal_prep_time: "Finding time to prepare meals", food_cost: "Food cost or availability", hunger_cravings: "Hunger or cravings", eating_out: "Eating outside the home", work_family: "Work or family commitments", appetite_symptoms: "Appetite or symptoms", medication_concerns: "Medication concerns", no_major: "No major challenges", other: "Something else" },
  symptoms_status: { no: "No", yes: "Yes", discuss: "Prefers to discuss with dietitian" },
  support_needed: { meal_plan_advice: "Advice on meal plan", portions_choices: "Portions and food choices", consistency: "Staying consistent", progress_measurements: "Discuss progress and measurements", none: "No additional support needed" },
  progress_feeling: { significant: "Significant progress", some: "Some progress", little: "Little or no progress", unsure: "Not sure yet" },
  next_step: { complete: "Complete programme for now", continue: "Continue with PlateMind", book_session: "Book a session before deciding" },
};

const WEEKLY_ROWS = [["feeling", "Feeling"], ["adherence", "Meal plan (1-5)"], ["biggest_challenge", "Biggest challenge"], ["challenge_other", "Challenge detail"], ["symptoms_status", "New symptoms"], ["support_needed", "Support wanted"], ["notes", "Notes"]];
const REVIEW_ROWS = [["experience_rating", "Experience (1-5)"], ["progress_feeling", "Progress"], ["most_helpful", "Most helpful"], ["most_difficult", "Most difficult / improve"], ["next_step", "Next step"], ["anything_else", "Anything else"]];

export const checkinWhatsApp = (c) => {
  const phone = (c.patient_phone || window.prompt(`WhatsApp number for ${c.patient_name} (with country code):`) || "").replace(/[^0-9]/g, "");
  if (!phone) { toast.error("No phone number."); return; }
  const first = (c.patient_name || "").split(" ")[0];
  const form = shareLink(`/checkin/${c.token}`);
  const book = shareLink(`/consultation?type=follow_up&token=${c.token}&week=${c.week_number}`);
  const msg = c.checkin_type === "programme_review"
    ? `Hello ${first}, you've reached Week 12 of your PlateMind nutrition programme.\n\nThank you for being part of this journey. We would love to hear how the experience has been for you, what you've learned and what support you may need next.\n\nPlease complete your programme review here:\n${form}`
    : `Hello ${first}, it's time for your Week ${c.week_number} PlateMind check-in.\n\nHow have you been feeling, and how are you getting on with your nutrition plan? Please complete your weekly check-in so your dietitian can understand your progress and any challenges.\n\nComplete weekly check-in:\n${form}\n\nIf you'd like to discuss your progress, ask questions or adjust your plan, you can also book a follow-up session:\n${book}`;
  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
};

export const CheckinAnswers = ({ checkin }) => {
  const rows = checkin.checkin_type === "programme_review" ? REVIEW_ROWS : WEEKLY_ROWS;
  const r = checkin.response || {};
  return (
    <div className="mt-3 pt-3 border-t border-stone-100 space-y-1.5 text-sm" data-testid={`answers-${checkin.checkin_id}`}>
      {rows.filter(([k]) => r[k] !== undefined && r[k] !== null && r[k] !== "").map(([k, label]) => (
        <div key={k}><span className="text-xs font-semibold text-stone-400">{label}: </span>
          <span className={`text-stone-700 ${k === "symptoms_status" && r[k] === "yes" ? "text-red-600 font-medium" : ""}`}>{LABELS[k]?.[r[k]] || String(r[k])}</span></div>
      ))}
      {checkin.continuation_status && <Badge variant="outline" className="text-xs capitalize">{checkin.continuation_status.replace(/_/g, " ")}</Badge>}
      {checkin.triage_flags?.length > 0 && (
        <div className="pt-2">{checkin.triage_flags.map((f, i) => <p key={i} className="text-xs text-red-600">- {f.reason}</p>)}</div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = "text-stone-900", onClick, testid }) => (
  <button onClick={onClick} className="text-left bg-white border border-stone-200/70 rounded-xl px-3 py-2.5 hover:border-[#FF7A00]/40 transition-colors" data-testid={testid}>
    <p className={`text-xl font-bold ${tone}`}>{value}</p>
    <p className="text-[11px] text-stone-500 leading-tight">{label}</p>
  </button>
);

export const ProgrammePipeline = ({ stats }) => {
  const navigate = useNavigate();
  const p = stats.programme, b = stats.bookings || {};
  const toProgramme = () => navigate("/admin/programme");
  return (
    <Card className="rounded-xl border-stone-200/60 p-4 space-y-3 bg-stone-50/50" data-testid="programme-pipeline">
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Weekly check-ins (Weeks 2-11)</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label="Scheduled" value={stats.scheduled} testid="pipe-scheduled" />
          <Stat label="Sent, pending" value={stats.sent} tone="text-blue-600" testid="pipe-sent" />
          <Stat label="Completed" value={stats.completed} tone="text-green-600" testid="pipe-completed" />
          <Stat label="Missed" value={p.missed} tone="text-red-500" testid="pipe-missed" />
          <Stat label="Concerns to review" value={stats.triage.needs_review + stats.triage.priority + stats.triage.urgent} tone="text-[#FF7A00]" testid="pipe-concerns" />
          <Stat label="Unack. alerts" value={stats.unacknowledged_alerts} tone="text-red-500" testid="pipe-alerts" />
        </div>
      </div>
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Follow-up bookings</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label="Requested" value={b.booking_requested || 0} testid="pipe-book-requested" />
          <Stat label="In progress" value={b.booking_in_progress || 0} testid="pipe-book-progress" />
          <Stat label="Confirmed" value={b.confirmed || 0} tone="text-green-600" testid="pipe-book-confirmed" />
          <Stat label="Completed" value={b.completed || 0} testid="pipe-book-completed" />
          <Stat label="Cancelled" value={b.cancelled || 0} testid="pipe-book-cancelled" />
          <Stat label="Failed" value={b.failed || 0} tone="text-red-500" testid="pipe-book-failed" />
        </div>
      </div>
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Week 12 review and discharge</p>
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
          <Stat label="Reviews due in 7 days" value={p.upcoming_week12} testid="pipe-w12-upcoming" />
          <Stat label="Feedback to review" value={p.reviews_awaiting} tone="text-[#FF7A00]" testid="pipe-w12-awaiting" />
          <Stat label="Chose complete" value={p.chose_complete} testid="pipe-w12-complete" />
          <Stat label="Chose continue" value={p.chose_continue} testid="pipe-w12-continue" />
          <Stat label="Chose session" value={p.chose_book} testid="pipe-w12-book" />
          <Stat label="Discharge to approve" value={p.discharge_awaiting_approval} tone="text-purple-600" onClick={toProgramme} testid="pipe-discharge" />
          <Stat label="Renewals awaiting" value={p.renewals_awaiting} tone="text-cyan-600" onClick={toProgramme} testid="pipe-renewals" />
        </div>
      </div>
    </Card>
  );
};
